import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, DoorOpen, FileText, Pencil, Plus, Trash2, WifiOff } from 'lucide-react';
import { desktopApi } from '../lib/desktopApi';
import { WARNING_LEVELS } from '../lib/warningLevels';
import { formatStamp } from '../lib/time';
import { buildReportOptions, linkStatusText } from '../lib/noteReportLinks.mjs';
import WarningBadge from './WarningBadge';

function stamp(value) {
  if (!value) return '';
  try {
    return formatStamp(value);
  } catch (error) {
    return '';
  }
}

function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
}

const LevelPicker = ({ value, onChange, disabled }) => (
  <fieldset disabled={disabled} className="space-y-2">
    <legend className="mb-2 text-sm font-semibold text-muted-foreground">Avertissement (facultatif)</legend>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <button
        type="button"
        onClick={() => onChange(null)}
        aria-pressed={!value}
        data-testid="level-none"
        className={`min-h-12 rounded-lg border px-3 text-left text-[15px] font-semibold ${!value ? 'border-primary bg-primary/15' : 'border-border bg-card'}`}
      >
        Aucun avertissement
      </button>
      {WARNING_LEVELS.map((item) => (
        <button
          key={item.level}
          type="button"
          onClick={() => onChange(item.level)}
          aria-pressed={value === item.level}
          data-testid={`level-${item.level}`}
          className={`flex min-h-12 items-center gap-3 rounded-lg border px-3 text-left text-[15px] ${value === item.level ? `border-transparent ring-2 ${item.ring} bg-card` : 'border-border bg-card'}`}
        >
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-black ${item.badge}`}>{item.level}</span>
          <span className="leading-tight">{item.label}</span>
        </button>
      ))}
    </div>
  </fieldset>
);

const NoteForm = ({ initial, allowLevel, reports, drafts, onSubmit, onCancel, busy, submitLabel }) => {
  const [body, setBody] = useState(initial?.body || '');
  const [level, setLevel] = useState(initial?.warning_level || null);
  const [reportId, setReportId] = useState(initial?.report_id || '');
  const options = buildReportOptions({
    drafts, reports, currentId: initial?.report_id || '', currentStatus: initial?.report_status || null,
  });
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!body.trim()) {
          toast.error('Écrivez la note');
          return;
        }
        onSubmit({ body, warning_level: allowLevel ? level : null, report_id: reportId || null });
      }}
      className="space-y-4 rounded-lg border border-primary/40 bg-card p-4"
      data-testid="note-form"
    >
      <label className="block">
        <span className="mb-2 block text-sm font-semibold text-muted-foreground">Note</span>
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={4}
          maxLength={4000}
          placeholder="Ce qui s'est passé, ce qui a été fait"
          data-testid="note-body"
          className="w-full rounded-lg border border-input bg-background p-3 text-[17px] focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </label>
      {allowLevel && <LevelPicker value={level} onChange={setLevel} disabled={busy} />}
      <label className="block">
        <span className="mb-2 block text-sm font-semibold text-muted-foreground">Lier à un rapport (facultatif)</span>
        <select
          value={reportId}
          onChange={(event) => setReportId(event.target.value)}
          data-testid="note-report"
          className="h-12 w-full rounded-lg border border-input bg-background px-3 text-[16px]"
        >
          <option value="">Aucun rapport</option>
          {options.current.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          {options.drafts.length > 0 && (
            <optgroup label="Rapport en cours">
              {options.drafts.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </optgroup>
          )}
          {options.reports.length > 0 && (
            <optgroup label="Rapports envoyés">
              {options.reports.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </optgroup>
          )}
        </select>
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="submit"
          disabled={busy}
          data-testid="note-save"
          className="min-h-12 flex-1 rounded-lg bg-primary px-4 text-[16px] font-bold text-primary-foreground disabled:opacity-60"
        >
          {busy ? 'Enregistrement…' : submitLabel}
        </button>
        <button type="button" onClick={onCancel} className="min-h-12 rounded-lg border border-border px-4 text-[16px]">
          Annuler
        </button>
      </div>
    </form>
  );
};

/**
 * Historique et ajout de notes pour un étudiant (student) ou une chambre (room).
 * Lecture et ajout : tout agent connecté. Modification et suppression : l'auteur ou un admin (vérifié côté serveur).
 */
const FollowupNotes = ({ student, room, currentAgent, onBack, onChanged, onOpenRoom }) => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState(null);
  const [failed, setFailed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [confirmId, setConfirmId] = useState('');
  const [busy, setBusy] = useState(false);
  const [reports, setReports] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const targetRoom = student ? student.room : room;
  const studentId = student?.id || '';

  const load = useCallback(async () => {
    try {
      const list = studentId ? await desktopApi.getStudentNotes(studentId) : await desktopApi.getRoomNotes(room);
      setNotes(list);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [studentId, room]);

  useEffect(() => {
    setNotes(null);
    setAdding(false);
    setEditingId('');
    load();
  }, [load]);

  useEffect(() => {
    if (!adding && !editingId) return;
    // Rechargés à chaque ouverture du formulaire : le brouillon en cours peut avoir été envoyé entre-temps.
    desktopApi.getNoteDraftLinks().then((list) => setDrafts(Array.isArray(list) ? list : [])).catch(() => setDrafts([]));
    if (reports.length) return;
    desktopApi.getReports({ limit: 20 }).then((list) => setReports(Array.isArray(list) ? list : [])).catch(() => setReports([]));
  }, [adding, editingId, reports.length]);

  const canChange = (note) => Boolean(currentAgent?.isAdmin) || (currentAgent?.id && note.author_id === currentAgent.id);

  const create = async (values) => {
    setBusy(true);
    try {
      await desktopApi.createFollowupNote({ ...values, student_id: student?.id || null, room: student ? null : room });
      toast.success('Note ajoutée');
      setAdding(false);
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(errorText(error, 'Note non enregistrée (réseau ?)'));
    } finally {
      setBusy(false);
    }
  };

  const save = async (note, values) => {
    setBusy(true);
    try {
      await desktopApi.updateFollowupNote(note.id, values);
      toast.success('Note modifiée');
      setEditingId('');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(errorText(error, 'Modification impossible'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (note) => {
    setBusy(true);
    try {
      await desktopApi.deleteFollowupNote(note.id);
      toast.success('Note supprimée');
      setConfirmId('');
      await load();
      onChanged?.();
    } catch (error) {
      toast.error(errorText(error, 'Suppression impossible'));
    } finally {
      setBusy(false);
    }
  };

  const currentLevel = student?.warning_level || null;

  return (
    <section data-testid="followup-notes">
      <button
        type="button"
        onClick={onBack}
        data-testid="notes-back"
        className="mb-3 flex min-h-11 items-center gap-2 text-muted-foreground hover:text-primary"
      >
        <ArrowLeft className="h-4 w-4" /> Liste des étudiants
      </button>
      <div className="mb-4 rounded-lg border border-border bg-card p-4">
        <div className="flex items-start gap-4">
          <span className="flex h-14 min-w-16 shrink-0 items-center justify-center rounded-md bg-primary/15 px-2 text-2xl font-black text-primary">
            {targetRoom}{student?.bed ? <span className="ml-1 text-base">{student.bed}</span> : null}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xl font-bold leading-tight" data-testid="notes-title">
              {student
                ? <><span className="uppercase">{student.last_name}</span> {student.first_name}</>
                : `Chambre ${room}`}
            </p>
            {student?.departure && <p className="mt-1 text-sm text-muted-foreground">Départ le {student.departure}</p>}
            {student && (
              <div className="mt-2">
                {currentLevel
                  ? <WarningBadge level={currentLevel} withLabel />
                  : <span className="text-sm text-muted-foreground">Aucun avertissement</span>}
              </div>
            )}
          </div>
        </div>
        {student && onOpenRoom && (
          <button
            type="button"
            onClick={() => onOpenRoom(student.room)}
            data-testid="notes-open-room"
            className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-border px-4 text-[15px] font-semibold hover:border-primary/60"
          >
            <DoorOpen className="h-5 w-5" /> Historique de la chambre {student.room}
          </button>
        )}
      </div>

      {!adding && (
        <button
          type="button"
          onClick={() => { setAdding(true); setEditingId(''); }}
          data-testid="note-add"
          className="mb-4 flex min-h-14 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-[17px] font-bold text-primary-foreground"
        >
          <Plus className="h-5 w-5" /> Ajouter une note
        </button>
      )}
      {adding && (
        <div className="mb-4">
          <NoteForm
            allowLevel={Boolean(student)}
            reports={reports}
            drafts={drafts}
            busy={busy}
            submitLabel="Enregistrer la note"
            onSubmit={create}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}

      <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-primary">
        {student ? 'Historique de l\'étudiant' : 'Historique de la chambre'}
      </h2>
      {failed && (
        <p className="mb-3 flex items-center gap-2 rounded-md border border-border bg-zinc-900/60 px-3 py-2 text-sm text-muted-foreground">
          <WifiOff className="h-4 w-4 shrink-0" /> Notes disponibles avec du réseau seulement.
        </p>
      )}
      {!failed && notes === null && <p className="text-muted-foreground">Chargement…</p>}
      {notes && notes.length === 0 && <p className="text-muted-foreground" data-testid="notes-empty">Aucune note pour l'instant.</p>}
      <ul className="space-y-3">
        {(notes || []).map((note) => (
          <li key={note.id} className="rounded-lg border border-border bg-card p-4" data-testid="note-item">
            {editingId === note.id ? (
              <NoteForm
                initial={note}
                allowLevel={Boolean(note.student_id)}
                reports={reports}
                drafts={drafts}
                busy={busy}
                submitLabel="Enregistrer"
                onSubmit={(values) => save(note, values)}
                onCancel={() => setEditingId('')}
              />
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">{stamp(note.created_at)}</span>
                  <span>· {note.author_name || 'Agent'}</span>
                  {note.warning_level ? <WarningBadge level={note.warning_level} /> : null}
                </div>
                {!student && note.student_name && (
                  <p className="mt-1 text-sm font-semibold">{note.student_name}</p>
                )}
                <p className="mt-2 whitespace-pre-wrap text-[16px] leading-snug">{note.body}</p>
                {note.updated_by && note.updated_at !== note.created_at && (
                  <p className="mt-1 text-xs text-muted-foreground">Modifiée le {stamp(note.updated_at)} par {note.updated_by}</p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {note.report_id && linkStatusText(note) && (
                    <span className="flex min-h-11 items-center gap-2 rounded-md border border-dashed border-border px-3 text-sm text-muted-foreground" data-testid="note-link-status">
                      <FileText className="h-4 w-4" /> {linkStatusText(note)}
                    </span>
                  )}
                  {note.report_id && note.report_status !== 'draft' && note.report_status !== 'missing' && (
                    <button
                      type="button"
                      onClick={() => navigate(`/rapport/${note.report_id}`)}
                      className="flex min-h-11 items-center gap-2 rounded-md border border-border px-3 text-sm font-semibold hover:border-primary/60"
                    >
                      <FileText className="h-4 w-4" /> Voir le rapport
                    </button>
                  )}
                  {canChange(note) && confirmId !== note.id && (
                    <>
                      <button
                        type="button"
                        onClick={() => { setEditingId(note.id); setAdding(false); }}
                        data-testid="note-edit"
                        className="flex min-h-11 items-center gap-2 rounded-md border border-border px-3 text-sm hover:border-primary/60"
                      >
                        <Pencil className="h-4 w-4" /> Modifier
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(note.id)}
                        data-testid="note-delete"
                        className="flex min-h-11 items-center gap-2 rounded-md border border-border px-3 text-sm text-red-300 hover:border-red-500/60"
                      >
                        <Trash2 className="h-4 w-4" /> Supprimer
                      </button>
                    </>
                  )}
                  {confirmId === note.id && (
                    <div className="flex w-full flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        onClick={() => remove(note)}
                        disabled={busy}
                        data-testid="note-delete-confirm"
                        className="min-h-12 flex-1 rounded-lg bg-red-600 px-4 font-bold text-white"
                      >
                        Confirmer la suppression
                      </button>
                      <button type="button" onClick={() => setConfirmId('')} className="min-h-12 rounded-lg border border-border px-4">
                        Garder la note
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default FollowupNotes;
